import {Component, ElementRef, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {
  type CKEditorCloudConfig,
  type CKEditorCloudResult,
  CKEditorModule,
  loadCKEditorCloud
} from '@ckeditor/ckeditor5-angular';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ArticleService} from '../../../../core/services/article/article.service';
import {PigletArticleService} from '../../../../core/services/article/piglet-article.service';
import {PigletEditArticle} from '../../../../core/models/article/piglet-edit-article.model';
import {ArticleCategoryTypeModel} from '../../../../core/models/article/article-category-type.model';
import {ArticleCategoryModel} from '../../../../core/models/article/article-category.model';
import {PigletArticleListInfo} from '../../../../core/models/article/article-list-info.model';
import {environment} from '../../../../../environments/environment';

import type {ClassicEditor, EditorConfig} from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';

const cloudConfig = {
  version: '46.0.0'
} satisfies CKEditorCloudConfig;

@Component({
  selector: 'app-piglet-edit-article',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CKEditorModule,
    FormsModule
  ],
  templateUrl: './piglet-edit-article.component.html',
  styleUrl: './piglet-edit-article.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class PigletEditArticleComponent implements OnInit {

  public Editor: typeof ClassicEditor | null = null;
  public config: EditorConfig | null = null;
  // Form
  articleForm: FormGroup;
  // Data
  articleId: number | null = null;
  article: PigletArticleListInfo | null = null;
  categoryTypes: ArticleCategoryTypeModel[] = [];
  categories: ArticleCategoryModel[] = [];
  availableLanguages: string[] = [];
  allLanguages: string[];
  // UI state
  loading = false;
  submitting = false;
  error: string | null = null;
  // ckeditor
  @ViewChild('editorWordCountElement') private editorWordCount!: ElementRef<HTMLDivElement>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly articleService: ArticleService,
    private readonly pigletArticleService: PigletArticleService
  ) {
    this.allLanguages = this.translateService.getLangs();
    this.articleForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadCategoryTypes();

    // Get article ID from route params
    this.route.paramMap.subscribe(params => {
      const idParam = params.get('id');
      if (idParam) {
        this.articleId = parseInt(idParam, 10);
        this.loadArticle();
        return;
      }

      loadCKEditorCloud(cloudConfig).then(this._setupEditor.bind(this));
    });
  }

  onCategoryTypeChange(): void {
    const typeId = this.articleForm.get('categoryTypeId')?.value;
    if (typeId) {
      this.loadCategories(typeId);
    }
  }

  onLanguageChange(): void {
    if (!this.articleId || !this.article) return;

    const selectedLanguage = this.articleForm.get('language')?.value;

    // Check if the selected language is in the available languages
    if (selectedLanguage && this.availableLanguages.includes(selectedLanguage.toLowerCase())) {
      // Navigate to the same page with the selected language as a query parameter
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {language: selectedLanguage},
        queryParamsHandling: 'merge'
      });
    }
  }

  onSubmit(): void {
    if (this.articleForm.invalid || this.submitting) {
      return;
    }

    this.submitting = true;

    const formValue = this.articleForm.getRawValue();
    const article: PigletEditArticle = {
      articleId: this.articleId || 0,
      categoryId: formValue.categoryId,
      categoryTypeId: formValue.categoryTypeId,
      language: formValue.language,
      title: formValue.title,
      content: formValue.content,
      publishDate: formValue.publishDate,
      allowComments: formValue.allowComments,
      published: formValue.published,
      visible: formValue.visible
    };

    const isNewArticle = !this.articleId;

    this.pigletArticleService.editArticle(article).subscribe({
      next: (result) => {
        console.log(result);
        this.submitting = false;
        this.article = result;

        // If it's a new language, redirect to the same page with the new language
        if ((this.article && !this.article.availableLanguages.map(x => x.toLowerCase()).includes(formValue.language)) || isNewArticle) {
          this.router.navigate(['/piglet/articles/edit', result.id], {
            relativeTo: this.route,
            queryParams: {language: formValue.language},
            queryParamsHandling: 'merge'
          });
        } else {
          // Otherwise, reload the page
          window.location.reload();
        }
      },
      error: (err) => {
        this.error = 'Failed to save article';
        this.submitting = false;
        console.error(err);
      }
    });
  }

  public onReady(editor: ClassicEditor): void {
    Array.from(this.editorWordCount.nativeElement.children).forEach(child => child.remove());

    const wordCount = editor.plugins.get('WordCount');
    this.editorWordCount.nativeElement.appendChild(wordCount.wordCountContainer);
  }

  private createForm(): FormGroup {
    return this.fb.group({
      articleId: [{value: '', disabled: true}],
      name: ['', Validators.required],
      categoryTypeId: ['', Validators.required],
      categoryId: ['', Validators.required],
      language: ['', Validators.required],
      author: [{value: '', disabled: true}],
      publishDate: [new Date().toISOString().slice(0, 16)],
      allowComments: [true],
      published: [true],
      visible: [true],
      title: ['', Validators.required],
      content: ['', Validators.required],
      thumbnailImage: [''],
      backgroundImage: ['']
    });
  }

  private loadCategoryTypes(): void {
    this.loading = true;
    this.articleService.getArticleCategoryTypes().subscribe({
      next: (types) => {
        this.categoryTypes = types;
        this.loading = false;

        if (types.length > 0 && !this.articleForm.get('categoryTypeId')?.value) {
          this.articleForm.get('categoryTypeId')?.setValue(types[0].categoryId);
          this.onCategoryTypeChange();
        }
      },
      error: (err) => {
        this.error = 'Failed to load category types';
        this.loading = false;
        console.error(err);
      }
    });
  }

  private loadCategories(typeId: number): void {
    this.loading = true;
    this.articleService.getArticleCategoriesByType(typeId).subscribe({
      next: (categories) => {
        this.categories = categories;
        this.loading = false;

        if (categories.length > 0 && !this.articleForm.get('categoryId')?.value) {
          this.articleForm.get('categoryId')?.setValue(categories[0].categoryId);
        }
      },
      error: (err) => {
        this.error = 'Failed to load categories';
        this.loading = false;
        console.error(err);
      }
    });
  }

  private loadArticle(): void {
    // Get language from query params, or use current language
    this.route.queryParamMap.subscribe(params => {
      if (!this.articleId) {
        return;
      }

      const language = params.get('language') || this.translateService.currentLang || 'pt-br';

      this.loading = true;
      this.pigletArticleService.getArticle(this.articleId, language).subscribe({
        next: (article) => {
          this.article = article;
          this.availableLanguages = article.availableLanguages.map(x => x.toLowerCase());
          this.populateForm(article, language);
          loadCKEditorCloud(cloudConfig).then(this._setupEditor.bind(this));
          this.loading = false;
        },
        error: (err) => {
          this.error = 'Failed to load article';
          this.loading = false;
          console.error(err);
        }
      });
    });
  }

  private populateForm(article: PigletArticleListInfo, language: string): void {
    // Set category type and load categories
    const categoryTypeId = this.getCategoryTypeIdForCategory(article.category);
    if (categoryTypeId) {
      this.articleForm.get('categoryTypeId')?.setValue(categoryTypeId);
      this.onCategoryTypeChange();
    }

    // Populate form with article data
    this.articleForm.patchValue({
      articleId: article.id,
      name: article.title,
      language: language,
      author: article.author,
      publishDate: new Date(article.createTime).toISOString().slice(0, 16),
      allowComments: article.allowComments,
      published: article.isPublished,
      visible: !article.isHidden,
      title: article.title,
      content: article.content,
      thumbnailImage: article.thumbnailImage,
      backgroundImage: article.backgroundImage
    });
  }

  private getCategoryTypeIdForCategory(categoryName: string): number | null {
    // This is a placeholder. In a real implementation, you would need to determine
    // the category type ID based on the category name or get it from the API.
    return this.categoryTypes.length > 0 ? this.categoryTypes[0].categoryId : null;
  }

  private _setupEditor(cloud: CKEditorCloudResult<typeof cloudConfig>) {
    const {
      ClassicEditor,
      Alignment,
      Autoformat,
      AutoImage,
      AutoLink,
      Autosave,
      BalloonToolbar,
      BlockQuote,
      Bold,
      Bookmark,
      Code,
      CodeBlock,
      Emoji,
      Essentials,
      FindAndReplace,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      FullPage,
      Fullscreen,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      HtmlComment,
      HtmlEmbed,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsert,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      ListProperties,
      MediaEmbed,
      Mention,
      Paragraph,
      PasteFromOffice,
      PlainTableOutput,
      RemoveFormat,
      ShowBlocks,
      SimpleUploadAdapter,
      SourceEditing,
      SpecialCharacters,
      SpecialCharactersArrows,
      SpecialCharactersCurrency,
      SpecialCharactersEssentials,
      SpecialCharactersLatin,
      SpecialCharactersMathematical,
      SpecialCharactersText,
      Strikethrough,
      Style,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableLayout,
      TableProperties,
      TableToolbar,
      TextPartLanguage,
      TextTransformation,
      TodoList,
      Underline,
      WordCount
    } = cloud.CKEditor;

    this.Editor = ClassicEditor;
    this.config = {
      toolbar: {
        items: [
          'undo',
          'redo',
          '|',
          'sourceEditing',
          'showBlocks',
          'findAndReplace',
          'textPartLanguage',
          'fullscreen',
          '|',
          'heading',
          'style',
          '|',
          'fontSize',
          'fontFamily',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'subscript',
          'superscript',
          'code',
          'removeFormat',
          '|',
          'emoji',
          'specialCharacters',
          'horizontalLine',
          'link',
          'bookmark',
          'insertImage',
          'insertImageViaUrl',
          'mediaEmbed',
          'insertTable',
          'insertTableLayout',
          'highlight',
          'blockQuote',
          'codeBlock',
          'htmlEmbed',
          '|',
          'alignment',
          '|',
          'bulletedList',
          'numberedList',
          'todoList',
          'outdent',
          'indent'
        ],
        shouldNotGroupWhenFull: false
      },
      plugins: [
        Alignment,
        Autoformat,
        AutoImage,
        AutoLink,
        Autosave,
        BalloonToolbar,
        BlockQuote,
        Bold,
        Bookmark,
        Code,
        CodeBlock,
        Emoji,
        Essentials,
        FindAndReplace,
        FontBackgroundColor,
        FontColor,
        FontFamily,
        FontSize,
        FullPage,
        Fullscreen,
        GeneralHtmlSupport,
        Heading,
        Highlight,
        HorizontalLine,
        HtmlComment,
        HtmlEmbed,
        ImageBlock,
        ImageCaption,
        ImageInline,
        ImageInsert,
        ImageInsertViaUrl,
        ImageResize,
        ImageStyle,
        ImageTextAlternative,
        ImageToolbar,
        ImageUpload,
        Indent,
        IndentBlock,
        Italic,
        Link,
        LinkImage,
        List,
        ListProperties,
        MediaEmbed,
        Mention,
        Paragraph,
        PasteFromOffice,
        PlainTableOutput,
        RemoveFormat,
        ShowBlocks,
        SimpleUploadAdapter,
        SourceEditing,
        SpecialCharacters,
        SpecialCharactersArrows,
        SpecialCharactersCurrency,
        SpecialCharactersEssentials,
        SpecialCharactersLatin,
        SpecialCharactersMathematical,
        SpecialCharactersText,
        Strikethrough,
        Style,
        Subscript,
        Superscript,
        Table,
        TableCaption,
        TableCellProperties,
        TableColumnResize,
        TableLayout,
        TableProperties,
        TableToolbar,
        TextPartLanguage,
        TextTransformation,
        TodoList,
        Underline,
        WordCount
      ],
      balloonToolbar: ['bold', 'italic', '|', 'link', 'insertImage', '|', 'bulletedList', 'numberedList'],
      fontFamily: {
        supportAllValues: true
      },
      fontSize: {
        options: [10, 12, 14, 'default', 18, 20, 22],
        supportAllValues: true
      },
      fullscreen: {
        onEnterCallback: container =>
          container.classList.add(
            'editor-container',
            'editor-container_classic-editor',
            'editor-container_include-style',
            'editor-container_include-word-count',
            'editor-container_include-fullscreen',
            'main-container'
          )
      },
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph'
          },
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1'
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2'
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3'
          },
          {
            model: 'heading4',
            view: 'h4',
            title: 'Heading 4',
            class: 'ck-heading_heading4'
          },
          {
            model: 'heading5',
            view: 'h5',
            title: 'Heading 5',
            class: 'ck-heading_heading5'
          },
          {
            model: 'heading6',
            view: 'h6',
            title: 'Heading 6',
            class: 'ck-heading_heading6'
          }
        ]
      },
      htmlSupport: {
        allow: [
          {
            name: /^.*$/,
            styles: true,
            attributes: true,
            classes: true
          }
        ]
      },
      image: {
        toolbar: [
          'toggleImageCaption',
          'imageTextAlternative',
          '|',
          'imageStyle:inline',
          'imageStyle:wrapText',
          'imageStyle:breakText',
          '|',
          'resizeImage'
        ]
      },
      initialData: '',
      licenseKey: environment.ckeditor.licenseKey,
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
          toggleDownloadable: {
            mode: 'manual',
            label: 'Downloadable',
            attributes: {
              download: 'file'
            }
          }
        }
      },
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: true
        }
      },
      mention: {
        feeds: [
          {
            marker: '@',
            feed: [
              /* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
            ]
          }
        ]
      },
      menuBar: {
        isVisible: true
      },
      placeholder: 'Type or paste your content here!',
      style: {
        definitions: [
          {
            name: 'Article category',
            element: 'h3',
            classes: ['category']
          },
          {
            name: 'Title',
            element: 'h2',
            classes: ['document-title']
          },
          {
            name: 'Subtitle',
            element: 'h3',
            classes: ['document-subtitle']
          },
          {
            name: 'Info box',
            element: 'p',
            classes: ['info-box']
          },
          {
            name: 'CTA Link Primary',
            element: 'a',
            classes: ['button', 'button--green']
          },
          {
            name: 'CTA Link Secondary',
            element: 'a',
            classes: ['button', 'button--black']
          },
          {
            name: 'Marker',
            element: 'span',
            classes: ['marker']
          },
          {
            name: 'Spoiler',
            element: 'span',
            classes: ['spoiler']
          }
        ]
      },
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
      }
    };
  }
}
